
import { Avatar } from "@/components/ui/avatar";
import { User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { ReportDialog } from "./ReportDialog";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { VerificationBadge } from "./verification/VerificationBadge";
import { FriendRequestButton } from "./friends/FriendRequestButton";

interface PostHeaderProps {
  username: string;
  userId: string;
  category: 'general' | 'gratitude' | 'support' | 'encouragement';
  isAnonymous: boolean;
  createdAt: string;
}

const categoryColors = {
  general: "bg-blue-100 text-blue-800 border-blue-200",
  gratitude: "bg-orange-100 text-orange-800 border-orange-200",
  support: "bg-pink-100 text-pink-800 border-pink-200",
  encouragement: "bg-green-100 text-green-800 border-green-200"
};

export const PostHeader = ({ 
  username, 
  userId, 
  category, 
  isAnonymous, 
  createdAt 
}: PostHeaderProps) => {
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setCurrentUserId(session.user.id);
      }
    };
    
    fetchCurrentUser();
  }, []);

  return (
    <div className="flex flex-row items-center gap-4 pb-3">
      {isAnonymous ? (
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 shadow-md flex items-center justify-center">
          <User className="w-6 h-6 text-gray-500" />
        </div>
      ) : (
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-200 to-purple-200 flex items-center justify-center shadow-md border-2 border-white">
          <span className="text-lg font-semibold text-pink-600">{username.charAt(0).toUpperCase()}</span>
        </div>
      )}
      <div className="flex flex-col flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-lg">{isAnonymous ? "Anonymous" : username}</span>
          {!isAnonymous && (
            <VerificationBadge userId={userId} size="sm" />
          )}
          <Badge variant="outline" className={`${categoryColors[category]} backdrop-blur-sm shadow-sm`}>
            {category}
          </Badge>
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {formatDistanceToNow(new Date(createdAt), { addSuffix: true })}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {currentUserId && currentUserId !== userId && !isAnonymous && (
          <FriendRequestButton 
            targetUserId={userId}
            currentUserId={currentUserId}
          />
        )}
        <ReportDialog
          contentType="post"
          contentId={userId}
          reportedUserId={userId}
          onReport={() => {}}
        />
      </div>
    </div>
  );
};
